import { useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { FirstWeekResponse } from 'aeo-schema-generator-shared/types'
import { apiService } from '@/services/api'

export const FIRST_WEEK_QUERY_KEY = (teamId: string) => ['first-week', teamId] as const

const IN_FLIGHT_POLL_MS = 4000

const isInFlight = (data: FirstWeekResponse | undefined) => !!data?.inFlight

/*
  The first-week guide's state. Refetched on window focus and after any step
  action; polled every few seconds only while a deploy check is in flight, so
  the live step turns Confirmed in place the moment the schema is seen.

  The arrival source (`viewedSource`) is a fact about this mount, not about
  the team: it goes out with the very first fetch after a fresh mount and
  never again. The caller passes it only on the render that strips the
  `welcome` param. A remount that finds the guide already in the cache sends
  nothing, because that person did not just arrive. Switching teams keeps the
  same silence: the key changes and the new team's first fetch carries no
  source, since the arrival already happened on this mount.
*/
export function useFirstWeek(params: { teamId: string | undefined; enabled: boolean; viewedSource?: 'checkout' | 'dashboard' }) {
  const { teamId, enabled, viewedSource } = params
  const queryClient = useQueryClient()
  const queryKey = FIRST_WEEK_QUERY_KEY(teamId ?? 'none')

  // Decided on the first render, before react-query schedules any fetch: a
  // cache hit means the source was sent by an earlier mount. An effect would
  // run after the mount fetch is already on its way.
  const sentViewRef = useRef<boolean | null>(null)
  if (sentViewRef.current === null) {
    sentViewRef.current = !!teamId && queryClient.getQueryData(queryKey) !== undefined
  }

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const viewed = sentViewRef.current ? undefined : viewedSource
      sentViewRef.current = true
      const response = await apiService.getFirstWeek(viewed)
      return response.data as FirstWeekResponse
    },
    enabled: enabled && !!teamId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (isInFlight(q.state.data) ? IN_FLIGHT_POLL_MS : false),
  })

  const pollingMs = isInFlight(query.data) ? IN_FLIGHT_POLL_MS : false

  return { guide: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch, pollingMs }
}
